import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const DisabilitySupport = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🦽",
      title: "Mobility Aids & Equipment",
      desc: "Providing wheelchairs, walkers, crutches, prosthetic limbs, and other mobility devices to enhance independence and quality of life.",
    },
    {
      icon: "🧠",
      title: "Rehabilitation & Therapy",
      desc: "Offering physiotherapy, occupational therapy, speech therapy, and counselling services to help individuals regain skills and confidence.",
    },
    {
      icon: "🔧",
      title: "Vocational Training & Employment",
      desc: "Empowering persons with disabilities through skill development, job placement assistance, and entrepreneurship support for financial independence.",
    },
    {
      icon: "🏠",
      title: "Accessible Infrastructure",
      desc: "Advocating for and assisting in the creation of accessible homes, workplaces, and public spaces with ramps, handrails, and sensory-friendly designs.",
    },
    {
      icon: "📚",
      title: "Education & Inclusive Learning",
      desc: "Supporting inclusive education through special educators, assistive technology, Braille materials, and sign language interpreters.",
    },
    {
      icon: "⚖️",
      title: "Rights Awareness & Legal Aid",
      desc: "Educating persons with disabilities about their legal rights, government schemes, and providing legal assistance to ensure equal opportunities and protection.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "1,200+", label: "Individuals Supported" },
    { number: "850+", label: "Mobility Aids Distributed" },
    { number: "400+", label: "Rehabilitation Sessions" },
    { number: "200+", label: "Jobs & Trainings Provided" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "🦽", title: "Donate Equipment", desc: "Fund wheelchairs, hearing aids, or other assistive devices." },
    { icon: "🤝", title: "Volunteer", desc: "Offer your time as a therapist, trainer, or companion." },
    { icon: "🏢", title: "Partner", desc: "Collaborate with us to create accessible spaces and opportunities." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="disability-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-wheelchair mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Support for Persons with Disabilities
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Empowering every individual to live with dignity, independence, and equal opportunities
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Empowering Abilities, Breaking Barriers
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Persons with disabilities are often marginalized and face physical, social, and economic barriers that
              limit their potential. At Venkatadri Seva Trust, our <strong>Support for Persons with Disabilities</strong>
              programme is committed to breaking these barriers and fostering an inclusive society where every
              individual can thrive regardless of their abilities.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              We provide comprehensive assistance—from mobility aids and rehabilitation to education, vocational
              training, and legal advocacy. Our approach is holistic and person-centered, ensuring that each
              individual receives the support they need to lead a life of dignity, independence, and purpose.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                A barrier‑free world where every person with a disability is respected, included, and empowered
                to achieve their full potential—participating fully in all aspects of life.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To provide holistic support—physical, emotional, educational, and economic—to persons with
                disabilities, while advocating for their rights and promoting an inclusive society.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Creating opportunities and removing obstacles for a more inclusive world
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Your support can transform lives and create a more inclusive community.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Inclusion Today
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              I lost my leg in an accident and thought my life was over. Venkatadri Seva Trust gave me a prosthetic
              limb, physiotherapy, and even helped me start a small business. Today, I am independent and proud.
              They restored my dignity.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Ravi, 34</p>
              <p className="text-yellow-100/70 text-sm">Bengaluru</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "Inclusion is not a favour; it is a fundamental right."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us break barriers,
              provide essential services, and advocate for a more equitable society for persons with disabilities.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, we can build a world where everyone, regardless of ability, can live with dignity and
              opportunity.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default DisabilitySupport;